import { useTranslation } from 'react-i18next';

import { Button, Tabs } from '@/components/ui/daisy';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

import { PendenciesTabEnum } from '@/pages/pendencies/PendenciesPage';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/utils/routes';
import User from '@/models/User';
import { useState } from 'react';
import { PendenciesDashboardTab } from './tabs/PendenciesDashboardTab';
import { ActivitiesDashboardTab } from './tabs/ActivitiesDashboardTab';
import { ExamsDashboardTab } from './tabs/ExamsDashboardTab';

type PendenciesProps = {
  user: User;
};

const tableClassNames = {
  container: 'rounded-2xl border border-base-content/5 bg-base-200',
  header: 'text-xs',
};

const tableClassName = 'w-full min-w-[1050px]';

const PENDENCIES_TABS_PAGE_SIZE = 6;

export function TeacherPendenciesSection({ user }: PendenciesProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });

  const [tab, setTab] = useState<PendenciesTabEnum>(
    PendenciesTabEnum.ATTENDANCE,
  );

  return (
    <section className="flex flex-1 flex-col gap-6 rounded-2xl bg-base-100 p-6">
      <header className="flex flex-col gap-2">
        <h2 className="text-xl font-medium leading-8 text-base-content">
          {t('dashboardTitle')}
        </h2>
      </header>
      <div className="flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-0">
        <Tabs
          variant="box"
          className="w-fit"
          value={tab}
          onChange={value => setTab(value as PendenciesTabEnum)}
        >
          <Tabs.Tab value={PendenciesTabEnum.ATTENDANCE}>
            {t('tabs.attendance')}
          </Tabs.Tab>
          <Tabs.Tab value={PendenciesTabEnum.EXAMS}>{t('tabs.exams')}</Tabs.Tab>
          <Tabs.Tab value={PendenciesTabEnum.ACTIVITIES}>
            {t('tabs.activities')}
          </Tabs.Tab>
        </Tabs>
        <Link
          to={`${ROUTES.PENDENCIES.BASE}?tab=${tab}`}
          className="md:flex md:justify-end"
        >
          <Button variant="outline" size="sm" color="neutral" className="w-fit">
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            {t('viewAll')}
          </Button>
        </Link>
      </div>

      <ConditionalRenderer condition={tab === PendenciesTabEnum.ATTENDANCE}>
        <PendenciesDashboardTab
          user={user}
          tableClassNames={tableClassNames}
          tableClassName={tableClassName}
          emptyMessage={t('empty')}
          pageSize={PENDENCIES_TABS_PAGE_SIZE}
        />
      </ConditionalRenderer>

      <ConditionalRenderer condition={tab === PendenciesTabEnum.EXAMS}>
        <ExamsDashboardTab
          user={user}
          tableClassNames={tableClassNames}
          tableClassName={tableClassName}
          emptyMessage={t('empty')}
          pageSize={PENDENCIES_TABS_PAGE_SIZE}
        />
      </ConditionalRenderer>

      <ConditionalRenderer condition={tab === PendenciesTabEnum.ACTIVITIES}>
        <ActivitiesDashboardTab
          user={user}
          tableClassNames={tableClassNames}
          tableClassName={tableClassName}
          emptyMessage={t('empty')}
          pageSize={PENDENCIES_TABS_PAGE_SIZE}
        />
      </ConditionalRenderer>
    </section>
  );
}
